import React, { useEffect, useState } from "react";
import { firestoreDB } from "../firebase";
import { doc, getDoc, setDoc, arrayUnion, arrayRemove } from "firebase/firestore";
import { 
  Shield, 
  Mail, 
  Cloud, 
  ExternalLink, 
  User, 
  Edit3, 
  Check, 
  X, 
  Plus, 
  UserMinus, 
  Sliders,
  HelpCircle 
} from "lucide-react";
import "./AdminProfile.css";

function AdminProfile() {
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [staffEmails, setStaffEmails] = useState([]);
  const [newStaffEmail, setNewStaffEmail] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [backupProfile, setBackupProfile] = useState(null);

  const adminDocRef = doc(firestoreDB, "Admins", "profile");

  const links = [
    {
      title: "Mobile Application",
      desc: "Access the latest builds and assets in the Mega cloud folder.",
      url: "https://mega.nz/folder/UBgSHYiI#AHkpX4lJSi0ICGe8xNoImQ",
      variant: "mega",
      icon: <Shield size={20} />,
    },
    {
      title: "Internal Email",
      desc: "Login to the enterprise mail server for gold hosting services.",
      url: "https://gold.mudahhosting.com:2096/",
      variant: "mail",
      icon: <Mail size={20} />,
    },
    {
      title: "Google Cloud",
      desc: "Manage infrastructure and backend services via Google Console.",
      url: "https://console.cloud.google.com/",
      variant: "gcp",
      icon: <Cloud size={20} />,
    },
  ];

  // Fetch admin profile details on structural mount
  useEffect(() => {
    const loadAdminProfile = async () => {
      try {
        setLoading(true);
        const snap = await getDoc(adminDocRef);
        if (snap.exists()) {
          const data = snap.data();
          setName(data.name || "");
          setPhone(data.phone || "");
          setEmail(data.email || "");
          setAddress(data.address || "");
          setStaffEmails(Array.isArray(data.staffEmails) ? data.staffEmails : []);
        }
      } catch (err) {
        console.error("Failed to load profile:", err);
      } finally {
        setLoading(false);
      }
    };

    loadAdminProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Safely run once on initialization

  // 🔐 Helper function to authenticate sensitive admin triggers
  const authenticateAdmin = () => {
    const password = prompt("Enter authorization password to proceed:");
    if (password === null) return false; 
    if (password !== "menara1234") {
      alert("Unauthorized entry. Access Denied.");
      return false;
    }
    return true;
  };

  // 💡 TRIGGERED ON CLICK: Instantly surfaces the required hint text without hover delays
  const handleShowHint = (e) => {
    e.stopPropagation(); // Prevents input element refocussing bugs
    alert("Password Hint: Menara Wifi Password");
  };

  // ⚡ OPTIMIZATION: Use Firestore atomic array operations to handle multiple fast inputs safely
  const handleAddStaffEmail = async () => {
    const mail = newStaffEmail.trim().toLowerCase();
    if (!mail) return;
    if (staffEmails.includes(mail)) return alert("Email already authorized.");

    // 🔐 Password Gate Check
    if (!authenticateAdmin()) return;

    setStaffEmails((prev) => [...prev, mail]);
    setNewStaffEmail(""); 

    try {
      await setDoc(adminDocRef, { staffEmails: arrayUnion(mail) }, { merge: true });
    } catch (err) {
      console.error("Failed to append staff token:", err);
      alert("System sync error occurred.");
      // Soft profile re-fetch fallback mechanism if state diverges
      const snap = await getDoc(adminDocRef);
      if (snap.exists()) setStaffEmails(snap.data().staffEmails || []);
    }
  };

  // ⚡ OPTIMIZATION: Safe removal handling via atomic token filtering
  const handleRemoveStaffEmail = async (index, targetEmail) => {
    if (!window.confirm(`Revoke gateway access for ${targetEmail}?`)) return;

    setStaffEmails((prev) => prev.filter((_, i) => i !== index));

    try {
      await setDoc(adminDocRef, { staffEmails: arrayRemove(targetEmail) }, { merge: true });
    } catch (err) {
      console.error("Failed to revoke privileges:", err);
      alert("Failed to sync structural changes.");
      const snap = await getDoc(adminDocRef);
      if (snap.exists()) setStaffEmails(snap.data().staffEmails || []);
    }
  };

  const handleEditProfile = () => {
    // 🔐 Password Gate Check
    if (!authenticateAdmin()) return;

    setBackupProfile({ name, phone, email, address });
    setEditMode(true);
  };

  const handleCancelEdit = () => {
    if (backupProfile) {
      setName(backupProfile.name);
      setPhone(backupProfile.phone);
      setEmail(backupProfile.email);
      setAddress(backupProfile.address);
    }
    setEditMode(false);
  };

  const handleSaveProfile = async () => {
    const profileData = { 
      name: name.trim(), 
      phone: phone.trim(), 
      email: email.trim(), 
      address: address.trim() 
    };

    try {
      await setDoc(adminDocRef, profileData, { merge: true });
      setEditMode(false);
    } catch (err) {
      alert("Failed to save profile modifications.");
    }
  };

  const handleOpen = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="ap-dashboard-root">
      <div className="ap-radial-blur blur-1"></div>
      <div className="ap-radial-blur blur-2"></div>

      <div className="ap-dashboard-container">
        <header className="ap-dashboard-header">
          <h1 className="ap-header-title">Profile Center</h1>
        </header>

        {loading ? (
          <div className="ap-loader-wrapper">
            <div className="ap-native-spinner"></div>
            <p>Syncing security infrastructure...</p>
          </div>
        ) : (
          <div className="ap-dashboard-grid">
            
            {/* Column 1: Core Form Controls */}
            <main className="ap-panel card-main">
              <div className="ap-panel-header">
                <div className="ap-panel-icon-title">
                  <User size={18} className="ap-icon-accent" />
                  <h2>Identity Details</h2>   
                </div>
                {!editMode && !loading && (
                  <div className="ap-action-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div 
                      className="ap-hint-container" 
                      onClick={handleShowHint}
                      style={{ cursor: 'pointer', color: 'var(--text-muted, #888)', display: 'flex', alignItems: 'center' }}
                    >
                      <HelpCircle size={16} />
                    </div>
                    <button className="ap-prime-edit-btn" onClick={handleEditProfile}>
                      <Edit3 size={16} /> <span>Modify Settings</span>
                    </button>
                  </div>
                )}
              </div>

              <div className={`form-matrix ${editMode ? "ap-matrix-editable" : ""}`}>
                <div className="ap-field-block">
                  <label>Full System Name</label>
                  <div className="ap-input-decoratorr">
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      readOnly={!editMode}
                      placeholder="Admin Account Identifier"
                    />
                  </div>
                </div>

                <div className="ap-field-block">
                  <label>Secure Contact Line</label>
                  <div className="ap-input-decoratorr">
                    <input
                      type="text"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      readOnly={!editMode}
                      placeholder="Telephone Contact"
                    />
                  </div>
                </div>

                <div className="ap-field-block full-span">
                  <label>Superadmin Routing Email</label>
                  <div className="ap-input-decoratorr">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      readOnly={!editMode}
                      placeholder="system@enterprise.com"
                    />
                  </div>
                </div>

                <div className="ap-field-block full-span">
                  <label>Physical/Headquarters Location</label>
                  <div className="ap-input-decoratorr">
                    <textarea
                      rows="3"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      readOnly={!editMode}
                      placeholder="Registered corporate routing address"
                    />
                  </div>
                </div>
              </div>

              {editMode && (
                <div className="ap-panel-actions-tray">
                  <button className="ap-btn-commit" onClick={handleSaveProfile}>
                    <Check size={16} /> Save Changes
                  </button>
                  <button className="ap-btn-abort" onClick={handleCancelEdit}>
                    <X size={16} /> Discard
                  </button>
                </div>
              )}
            </main>

            <div className="ap-dashboard-sidebar">
              
              {/* Column 2: Access Gateway Management */}
              <section className="ap-panel card-side">
                <div className="ap-panel-header">
                  <div className="ap-panel-icon-title">
                    <Sliders size={18} className="ap-icon-accent" />
                    <h2>Staff Directives</h2>
                  </div>
                  <span className="ap-count-badge">{staffEmails.length} Active</span>
                </div>

                <div className="ap-interactive-input-group" style={{ position: 'relative' }}>
                  <input
                    type="email"
                    placeholder="Authorize staff email address..."
                    value={newStaffEmail}
                    onChange={(e) => setNewStaffEmail(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleAddStaffEmail()}
                    style={{ paddingRight: '60px' }}
                  />
                  <div className="ap-input-actions-tray" style={{ position: 'absolute', right: '5px', top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <div 
                      className="ap-hint-container" 
                      onClick={handleShowHint}
                      style={{ cursor: 'pointer', color: 'var(--text-muted, #888)', display: 'flex', alignItems: 'center' }}
                    >
                      <HelpCircle size={14} />
                    </div>
                    <button className="ap-inline-add-btn" onClick={handleAddStaffEmail}>
                      <Plus size={16} />
                    </button>
                  </div>
                </div>

                <div className="ap-scrollable-manifest">
                  {staffEmails.length === 0 ? (
                    <div className="ap-null-state">
                      <p>No external personnel keys provisions found.</p>
                    </div>
                  ) : (
                    staffEmails.map((m, i) => (
                      <div key={i} className="ap-manifest-item">
                        <span className="ap-clamped-text">{m}</span>
                        <button 
                          className="ap-item-revoke-btn" 
                          onClick={() => handleRemoveStaffEmail(i, m)}
                          title="Revoke Gateway Privileges"
                        >
                          <UserMinus size={14} />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </section>

              {/* Column 3: Resource Gateways */}
              <section className="ap-panel card-side">
                <div className="ap-panel-header">
                  <div className="ap-panel-icon-title">
                    <ExternalLink size={18} className="ap-icon-accent" />
                    <h2>Resource Engines</h2>
                  </div>
                </div>

                <div className="ap-resource-matrix">
                  {links.map((link, index) => (
                    <div 
                      key={index} 
                      className={`ap-resource-tile variant-${link.variant}`} 
                      onClick={() => handleOpen(link.url)}
                    >
                      <div className="ap-tile-lead">
                        <div className="ap-tile-icon-shield">{link.icon}</div>
                        <div className="ap-tile-meta">
                          <h3>{link.title}</h3>
                          <p>{link.desc}</p>
                        </div>
                      </div>
                      <div className="ap-tile-arrow">
                        <ExternalLink size={14} />
                      </div>
                    </div>
                  ))}
                </div>
              </section>

            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminProfile;